// src/components/pages/Home.jsx

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer";
import CardSlider from "../../component/Events/CardSlider";
import { fetchGetItems } from "../../redux/createEvent/createEventAction/GetEventAction";
import { getFilteredEvents } from "../../redux/auth/action/filterSelector";
import { eventTypeChoices } from "../../assets/static/eventTypes";
import HomeHelmet from "../../component/MetaHelmet/HomeHelmet";
import MiddleBanner from "./BannerComponents/MiddleBanner";
import SlickBanner from "./BannerComponents/SlickBanner";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";

const PAGE_SIZE = 20;

const Home = () => {
  const dispatch = useDispatch();
  const [currentPageBySegment, setCurrentPageBySegment] = useState({});
  const today = new Date().setHours(0, 0, 0, 0);

  // Redux selectors
  const events = useSelector((state) => state.getEvent);
  const filteredEventsData = useSelector(getFilteredEvents);
  const filters = useSelector((state) => state.filters);
  const { loading: eventsLoading, error } = events;
  
  // Use filtered events if any filters are applied, otherwise use all events
  const hasActiveFilters = filters.name.trim() !== "" ||
    filters.category.trim() !== "" ||
    (filters.dateRange.start !== null || filters.dateRange.end !== null) ||
    filters.startTime.trim() !== "" ||
    filters.endTime.trim() !== "" ||
    filters.location.trim() !== "" ||
    filters.ticketPriceMin.trim() !== "" ||
    filters.ticketPriceMax.trim() !== "" ||
    filters.isFreeTicket ||
    filters.capacityMin.trim() !== "" ||
    filters.capacityMax.trim() !== "";

  const eventsResults = hasActiveFilters ? filteredEventsData.results : (events.data?.results || []);
  const allEvents = Array.isArray(eventsResults) ? eventsResults : [];

  // Filter out expired events
  const activeEvents = allEvents.filter(
    (event) => new Date(event.end_date).setHours(0, 0, 0, 0) >= today
  );

  // New Listings: events created in the last 3 days
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  const newListings = activeEvents.filter(event => {
    const createdAt = new Date(event.created_at);
    return createdAt >= threeDaysAgo;
  });

  // Remove new listings from the main event list to avoid duplicates
  const newListingIds = newListings.map(e => e.event_id);
  const restEvents = activeEvents.filter(e => !newListingIds.includes(e.event_id));

  // Group restEvents by segment
  const eventsBySegment = {};
  eventTypeChoices.forEach(type => {
    eventsBySegment[type.key] = restEvents.filter(event => event.event_segment === type.key);
  });

  // Pagination for each segment
  const getPaginatedEvents = (segmentKey) => {
    const page = currentPageBySegment[segmentKey] || 1;
    const events = eventsBySegment[segmentKey] || [];
    const totalPages = Math.ceil(events.length / PAGE_SIZE);
    const paginated = events.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return { paginated, totalPages, page };
  };

  // Fetch events on mount
  useEffect(() => {
    dispatch(fetchGetItems(1, 1000)); // Fetch a large number to get all events for grouping
  }, [dispatch]);

  // Pagination controls for each segment
  const Pagination = ({ segmentKey, totalPages, currentPage }) => (
    <div className="flex justify-center items-center gap-2 my-8">
      <button
        onClick={() => setCurrentPageBySegment(prev => ({ ...prev, [segmentKey]: currentPage - 1 }))}
        disabled={currentPage === 1}
        className={`p-2 rounded-full border transition ${
          currentPage === 1
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-white text-[#0056A6] hover:bg-[#FF9000] hover:text-white border-[#0056A6]"
        }`}
        aria-label="Previous Page"
      >
        <FaChevronLeft />
      </button>
      {[...Array(totalPages)].map((_, idx) => (
        <button
          key={idx}
          onClick={() => setCurrentPageBySegment(prev => ({ ...prev, [segmentKey]: idx + 1 }))}
          className={`px-3 py-1 rounded-full border transition font-semibold ${
            currentPage === idx + 1
              ? "bg-[#0056A6] text-white border-[#0056A6]"
              : "bg-white text-[#0056A6] border-[#0056A6] hover:bg-[#FF9000] hover:text-white"
          }`}
        >
          {idx + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPageBySegment(prev => ({ ...prev, [segmentKey]: currentPage + 1 }))}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-full border transition ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-white text-[#0056A6] hover:bg-[#FF9000] hover:text-white border-[#0056A6]"
        }`}
        aria-label="Next Page"
      >
        <FaChevronRight />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HomeHelmet />
      <Navbar />
      <main className="container mx-auto px-4 py-6">
        {/* Show filter status */}
        {hasActiveFilters && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">
              Showing {activeEvents.length} filtered results
              {filters.name && ` for "${filters.name}"`}
              {filters.category && ` in ${filters.category}`}
            </p>
          </div>
        )}

        {/* Banner */}
        <SlickBanner events={newListings.slice(0, 5)} />

        {/* New Listings Section */}
        {newListings.length > 0 && (
          <div className="mb-8">
            <p className="font-poppins text-lg font-semibold mt-7 uppercase">
              New Listings
            </p>
            <CardSlider
              className="relative"
              data={newListings}
              loading={eventsLoading}
            />
          </div>
        )}

        {/* Events by Segment */}
        <div>
          {eventTypeChoices.map((type, idx) => {
            const { paginated, totalPages, page } = getPaginatedEvents(type.key);
            if (paginated.length === 0) return null;

            return (
              <div key={type.key}>
                <p className="font-poppins text-lg font-semibold mt-7 uppercase">
                  {type.label}
                </p>
                <CardSlider
                  className="relative"
                  data={paginated}
                  loading={eventsLoading}
                />
                {totalPages > 1 && (
                  <Pagination
                    segmentKey={type.key}
                    totalPages={totalPages}
                    currentPage={page}
                  />
                )}
                {/* Optionally, show a banner in the middle */}
                {idx === Math.floor(eventTypeChoices.length / 2) && <MiddleBanner />}
              </div>
            );
          })}
        </div>

        {/* No results message */}
        {hasActiveFilters && activeEvents.length === 0 && (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-600 mb-4">No events found</h2>
            <p className="text-gray-500 mb-6">Try adjusting your search criteria or browse all events.</p>
            <Link 
              to="/" 
              onClick={() => dispatch({ type: 'RESET_FILTERS' })}
              className="bg-[#0056A6] text-white px-6 py-3 rounded-lg hover:bg-[#FF9000] transition-colors"
            >
              View All Events
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Home;
